'use client';

import React, { createContext, ReactNode, useContext, useMemo } from 'react';

/**
 * Interface for Public IDs Context data
 * Contains core identifiers available throughout the app (matching eb-ui pattern)
 */
interface PublicIdsContextData {
  eventId: string;
  organizationId: string;
  userId: string;
}

/**
 * Props for PublicIdsContextProvider component
 */
interface PublicIdsContextProviderProps {
  children: ReactNode;
  eventId: string;
  organizationId: string;
  userId: string;
}

const PublicIdsContext = createContext<PublicIdsContextData | undefined>(undefined);

/**
 * PublicIdsContextProvider - Provides global access to core identifiers (matching eb-ui pattern)
 *
 * This provider makes common identifiers available throughout the application
 * without prop drilling. The values are extracted from headers in the server-side
 * layout component and passed down as props.
 *
 * Matches eb-ui's PublicIdsContextProvider pattern for consistency during migration.
 *
 * @param children - Child components that will have access to the context
 * @param eventId - The current event ID from URL parameters
 * @param organizationId - The organization ID from request headers
 * @param userId - The user ID from request headers
 *
 * @example
 * ```tsx
 * // In layout.tsx (server component)
 * const eventId = params.eventId;
 * const organizationId = headersList.get(EventbriteHeader.EB_ORGANIZATION_ID);
 * const userId = headersList.get(EventbriteHeader.EB_USER_ID);
 *
 * <PublicIdsContextProvider eventId={eventId} organizationId={organizationId} userId={userId}>
 *   <MyApp />
 * </PublicIdsContextProvider>
 *
 * ```
 */
export const PublicIdsContextProvider: React.FC<PublicIdsContextProviderProps> = ({
  children,
  eventId,
  organizationId,
  userId,
}) => {
  const publicIdsContextData: PublicIdsContextData = useMemo(
    () => ({
      eventId,
      organizationId,
      userId,
    }),
    [eventId, organizationId, userId],
  );

  return (
    <PublicIdsContext.Provider value={publicIdsContextData}>{children}</PublicIdsContext.Provider>
  );
};

/**
 * Hook to access public IDs context values (matching eb-ui pattern)
 *
 * Provides access to eventId, organizationId, and userId from anywhere
 * in the component tree below PublicIdsContextProvider.
 *
 * @returns Object containing:
 * - `eventId`: The current event ID
 * - `organizationId`: The organization ID for the current user
 * - `userId`: The current user ID
 *
 * @throws Error if used outside of PublicIdsContextProvider
 *
 * @example
 * ```tsx
 * function PayoutComponent() {
 *   const { eventId, organizationId, userId } = usePublicIds();
 *
 *   const { data } = useQuery({
 *     queryKey: ['payouts', eventId, organizationId],
 *     queryFn: () => fetchPayouts(eventId, organizationId, userId)
 *   });
 *
 *   return <div>Payout data for event {eventId}</div>;
 * }
 * ```
 */
export const usePublicIds = (): PublicIdsContextData => {
  const context = useContext(PublicIdsContext);
  if (context === undefined) {
    throw new Error('usePublicIds must be used within a PublicIdsContextProvider');
  }
  return context;
};
